import type { Observable } from 'rxjs';
import { combineLatest, distinctUntilChanged, map, switchMap } from 'rxjs';
import { first } from 'lodash-es';
import type { AlarmLine, AlarmPoint } from '@/graphql/types';
import { BaseGraphElementsProvider } from './graph-elements/base-provider';
import { filterOnPropertyObservable } from '../utils/rxjs';
import { convertDistanceForDataType } from './data-types';
import type { Graph } from './graph';
import { SensorDataSetGraph } from './graph';
import type { DataSet } from './dataset';
import type { DataTypeValue } from './enums';
import type { Scales } from './graph-helpers';
import type { Profile } from '../profile';

export const ValueScaleEquation = Object.freeze({
    VIBRATION: 'vibration',
    ALARM: 'alarm',
    MAX: 'max',
    FIXED: 'fixed',
} as const);

export type ValueScaleEquationType = (typeof ValueScaleEquation)[keyof typeof ValueScaleEquation];
type AutoScaleEquationType = Exclude<ValueScaleEquationType, typeof ValueScaleEquation.FIXED>;

type MaxValuePerDataType = Record<DataTypeValue, number>;

interface EquationParams {
    dataType: DataTypeValue;
    maxValuePerDataType$: Observable<MaxValuePerDataType>;
    maxAlarm$: Observable<number>;
}

function maxValuePerDataType$(graphs: Graph[], dataSet: DataSet): Observable<MaxValuePerDataType> {
    const maxGraphValuesPerDataType$ = combineLatest(
        // Collect active plugins from all graph inputs:
        graphs
            // `graph` can also be a table; filter non-graph instances.
            .filter((graph): graph is SensorDataSetGraph => graph instanceof SensorDataSetGraph)
            .map((graph) => graph.activePlugins$)
    ).pipe(
        map((pluginsPerGraph) =>
            pluginsPerGraph
                // Concatenate all plugins from each graph into a single array.
                .flat()
                // Retain only plugins that provide graph elements.
                .filter(
                    (plugin): plugin is BaseGraphElementsProvider =>
                        plugin instanceof BaseGraphElementsProvider
                )
        ),
        // Filter out inactive plugins.
        switchMap((plugins) => filterOnPropertyObservable(plugins, 'isActive$')),
        // Keep unique `graphElements$` observables; some plugins share the same `graphElements$`.
        map((plugins) => Array.from(new Set(plugins.map((plugin) => plugin.graphElements$)))),
        // Listen to all `graphElements$` observables.
        switchMap((arrayOfGraphElements$) => combineLatest(arrayOfGraphElements$)),
        map((arrayOfElementArrays) =>
            arrayOfElementArrays.reduce((accumulator, elements) => {
                // Get the dataType from the first element in the array
                // (this dataType will be consistent across all entries).
                const firstElement = first(elements);
                const dataType = firstElement?.dataType;

                // Skip elements without a dataType, like events.
                if (dataType) {
                    // Determine the max value for each dataType and store it.
                    accumulator[dataType] = Math.max(
                        ...elements.map((element) => element.value!),
                        accumulator[dataType] ?? 0
                    );
                }

                return accumulator;
            }, {} as MaxValuePerDataType)
        )
    );

    return combineLatest([maxGraphValuesPerDataType$, dataSet.soundThresholdLines$]).pipe(
        map(([maxValuePerDataType, soundThresholdLines]) => {
            if (soundThresholdLines.length === 0) {
                return maxValuePerDataType;
            }

            return {
                ...maxValuePerDataType,
                [dataSet.dataType]: Math.max(
                    maxValuePerDataType[dataSet.dataType] ?? 0,
                    ...soundThresholdLines.map((line) => line.value)
                ),
            };
        })
    );
}

function maxAlarm$(dataSet: DataSet): Observable<number> {
    return combineLatest([
        dataSet.alarmLines$,
        dataSet.categoryLine$,
        dataSet.soundThresholdLines$,
    ]).pipe(
        map(([alarmLines, categoryLine, soundThresholdLines]) => {
            const allLines: Array<AlarmLine | AlarmPoint[] | number[][]> = [
                ...alarmLines,
                categoryLine,
            ];
            const maxPerLine = allLines.map((line) =>
                Array.isArray(line)
                    ? Math.max(
                          ...line.map((objOrArray) =>
                              Array.isArray(objOrArray) ? objOrArray[0] : objOrArray.vkar!
                          )
                      )
                    : 0
            );
            return Math.max(...maxPerLine, ...soundThresholdLines.map((line) => line.value));
        })
    );
}

function isAutoScaleEquation(
    valueScaleEquation: ValueScaleEquationType
): valueScaleEquation is AutoScaleEquationType {
    return valueScaleEquation !== ValueScaleEquation.FIXED;
}

const equations: Record<AutoScaleEquationType, (params: EquationParams) => Observable<number>> = {
    [ValueScaleEquation.VIBRATION](n: EquationParams): Observable<number> {
        return n.maxValuePerDataType$.pipe(
            map((maxValuePerDataType) => maxValuePerDataType[n.dataType] * 1.2)
        );
    },
    [ValueScaleEquation.ALARM](n: EquationParams): Observable<number> {
        return n.maxAlarm$.pipe(map((maxAlarm) => maxAlarm * 1.2));
    },
    [ValueScaleEquation.MAX](n: EquationParams): Observable<number> {
        return combineLatest([n.maxAlarm$, n.maxValuePerDataType$]).pipe(
            map(
                ([maxAlarm, maxValuePerDataType]) =>
                    Math.max(maxAlarm, maxValuePerDataType[n.dataType] ?? 0) * 1.2
            )
        );
    },
};

export function initAutoScaling(
    valueScale: number | null,
    dataSet: DataSet,
    scales: Scales,
    graphs: Graph[],
    valueScaleEquation: ValueScaleEquationType,
    dataType: DataTypeValue,
    profile: Profile
): void {
    // Perform auto-scaling only if no preferred fixed scale is set.
    if (valueScale || !isAutoScaleEquation(valueScaleEquation)) {
        // Preferred valueScale is set, skip auto-scaling.
        return;
    }

    equations[valueScaleEquation]({
        dataType,
        maxValuePerDataType$: maxValuePerDataType$(graphs, dataSet),
        maxAlarm$: maxAlarm$(dataSet),
    })
        .pipe(distinctUntilChanged())
        .subscribe((calculatedValueScale) => {
            if (!calculatedValueScale) {
                // Auto-scaling failed; no data available.
                return;
            }

            scales.set(
                dataType,
                convertDistanceForDataType(dataType, calculatedValueScale, profile)
            );
        });
}
